import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import styled from 'styled-components';
import { RichTextActiveFormats, RichTextFormat } from './rich-text.type';

const quillIcons = ReactQuill.Quill.import('ui/icons') as Record<
  string,
  string | Record<string, string>
>;

const listIcons = quillIcons.list as Record<string, string>;

type FormatSpec = {
  /** The editor's format name, which several controls share. */
  name: string;
  /** Sent when activating. Absent for plain on/off formats. */
  value?: string;
  icon: string;
  /** Translation key for the control's accessible name. */
  labelKey: string;
};

const FORMAT_SPECS: Record<RichTextFormat, FormatSpec> = {
  bold: {
    name: 'bold',
    icon: quillIcons.bold as string,
    labelKey: 'content.richText.bold',
  },
  italic: {
    name: 'italic',
    icon: quillIcons.italic as string,
    labelKey: 'content.richText.italic',
  },
  underline: {
    name: 'underline',
    icon: quillIcons.underline as string,
    labelKey: 'content.richText.underline',
  },
  link: {
    name: 'link',
    icon: quillIcons.link as string,
    labelKey: 'content.richText.link',
  },
  bullet: {
    name: 'list',
    value: 'bullet',
    icon: listIcons.bullet,
    labelKey: 'content.richText.bulletList',
  },
  ordered: {
    name: 'list',
    value: 'ordered',
    icon: listIcons.ordered,
    labelKey: 'content.richText.orderedList',
  },
  check: {
    name: 'list',
    value: 'check',
    icon: listIcons.check,
    labelKey: 'content.richText.checkList',
  },
};

const DEFAULT_GROUPS: RichTextFormat[][] = [
  ['bold', 'italic', 'underline'],
  ['link'],
];

const ToolbarContainer = styled.div`
  display: inline-flex;
  align-items: center;
  height: 55px;
  padding: var(--xsmall-padding);
  // Matches the slides editor toolbar — spec: 9px
  border-radius: 9px;
  border: 1px solid var(--gray-dark);
  background: var(--white-main);

  // Quill's icon SVGs colour themselves through these classes. The snow
  // stylesheet scopes them under .ql-snow, which this toolbar has no ancestor
  // of, so they are redeclared here with snow's own values.
  .ql-stroke,
  .ql-stroke-miter {
    fill: none;
    // Quill snow's icon stroke — no token matches exactly (nearest is
    // --gray-darkest #4d4d4d) and colours must not be rounded.
    stroke: #444;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .ql-fill,
  .ql-stroke.ql-fill {
    fill: #444;
  }

  .ql-empty {
    fill: none;
  }

  .ql-transparent {
    opacity: 0.4;
  }

  svg path,
  svg line {
    stroke-width: 1px;
  }

  .ql-formats {
    display: inline-flex;
    align-items: center;
    flex-wrap: nowrap;
    margin: var(--xxxsmall-padding);
    padding: var(--xxxsmall-padding);

    &:not(:last-child) {
      border-right: 1px solid var(--gray-moderate);
    }
  }
`;

/**
 * Shared button metrics, exported so controls passed in through
 * `trailingControls` sit at the same size as the format buttons.
 */
export const RichTextToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  // Quill snow's native toolbar button metrics
  width: 28px;
  height: 24px;
  padding: 3px 5px;
  margin-right: 5px;
  border: none;
  border-radius: var(--xxxsmall-border-radius);
  background: var(--beige-main);
  cursor: pointer;
  color: var(--black-main);

  &:last-child {
    margin-right: 0;
  }
`;

const FormatButton = styled(RichTextToolbarButton)<{
  $isActive: boolean;
  $disabled: boolean;
}>`
  background: ${({ $isActive }) =>
    $isActive ? 'var(--red-main-20)' : 'var(--beige-main)'};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
`;

type RichTextToolbarProps = {
  activeFormats: RichTextActiveFormats;
  disabled?: boolean;
  onFormat: (format: string, value?: unknown) => void;
  /** Rendered groups, separated by a divider. Defaults to B/I/U then link. */
  formatGroups?: RichTextFormat[][];
  /** Domain copy arrives as a prop — this module owns no feature wording. */
  linkPromptLabel?: string;
  /**
   * Controls specific to one editor's domain, rendered as a trailing group.
   * Anything the module has no generic notion of belongs here rather than in
   * the format list.
   */
  trailingControls?: React.ReactNode;
  className?: string;
  id?: string;
};

export const RichTextToolbar: React.FC<RichTextToolbarProps> = ({
  activeFormats,
  disabled = false,
  onFormat,
  formatGroups = DEFAULT_GROUPS,
  linkPromptLabel,
  trailingControls,
  className,
  id,
}) => {
  const { t } = useTranslation();
  const isFormatActive = (spec: FormatSpec) =>
    spec.value
      ? activeFormats[spec.name] === spec.value
      : !!activeFormats[spec.name];

  const handleFormat = (format: RichTextFormat) => {
    const spec = FORMAT_SPECS[format];

    if (format === 'link') {
      if (activeFormats.link) {
        onFormat('link', false);
        return;
      }
      const url = window.prompt(linkPromptLabel);
      if (url) onFormat('link', url);
      return;
    }

    // The checklist stores 'checked'/'unchecked', never the 'check' the control
    // carries, so it can only be cleared by inspecting the stored value.
    if (format === 'check') {
      const current = activeFormats.list;
      const isChecklist = current === 'checked' || current === 'unchecked';
      onFormat('list', isChecklist ? false : 'unchecked');
      return;
    }

    onFormat(spec.name, isFormatActive(spec) ? false : spec.value ?? true);
  };

  return (
    <ToolbarContainer
      id={id}
      className={className}
      onMouseDown={(event) => event.preventDefault()}
    >
      {formatGroups.map((group, groupIndex) => (
        <span className="ql-formats" key={`group-${groupIndex}`}>
          {group.map((format) => (
            <FormatButton
              key={format}
              type="button"
              aria-label={t(FORMAT_SPECS[format].labelKey)}
              aria-pressed={isFormatActive(FORMAT_SPECS[format])}
              disabled={disabled}
              $isActive={isFormatActive(FORMAT_SPECS[format])}
              $disabled={disabled}
              onClick={() => handleFormat(format)}
              dangerouslySetInnerHTML={{ __html: FORMAT_SPECS[format].icon }}
            />
          ))}
        </span>
      ))}
      {trailingControls && (
        <span className="ql-formats">{trailingControls}</span>
      )}
    </ToolbarContainer>
  );
};
